import { z } from 'zod';
import { SurfaceSchema } from './surfaces.js';
import { LanguageCodeSchema } from './types/common.js';
import { PatientBannerSchema } from './types/patient.js';
import { UserPreferencesSchema } from './types/tenant.js';

// ------------------------------------------------------------------ Chat
export const TranscriptMetaSchema = z.object({
  language: LanguageCodeSchema.optional(),
  /** Transcribe-reported identified language when auto identification is on. */
  identifiedLanguage: z.string().optional(),
  /** Mean item confidence of the final transcript, 0..1. */
  confidence: z.number().min(0).max(1).optional(),
  /** Exact streaming duration for cost tracking. */
  streamDurationMs: z.number().int().nonnegative(),
  voiceSessionId: z.string(),
});
export type TranscriptMeta = z.infer<typeof TranscriptMetaSchema>;

export const ChatRequestSchema = z.object({
  conversationId: z.string().optional(),
  message: z.string().min(1).max(4000),
  inputMode: z.enum(['text', 'voice']).default('text'),
  transcript: TranscriptMetaSchema.optional(),
  /** Set when the message is a structured form submission (contextual form surface). */
  form: z.object({ formId: z.string(), values: z.record(z.string(), z.string()) }).optional(),
  /** Client-side locale for responses; server may override with tenant default. */
  locale: LanguageCodeSchema.optional(),
  clientTimeZone: z.string().optional(),
});
export type ChatRequest = z.infer<typeof ChatRequestSchema>;

export const ChatResponseSchema = z.object({
  conversationId: z.string(),
  /** Primary textual reply, Markdown. */
  text: z.string(),
  /** Short, PHI-minimised sentence safe for text-to-speech; absent means do not speak. */
  speakableText: z.string().optional(),
  surfaces: z.array(SurfaceSchema),
  context: z.object({
    activePatient: PatientBannerSchema.nullable(),
    activeEncounterId: z.string().nullable(),
  }),
  pendingActionId: z.string().optional(),
  /** Diagnostic: tools invoked this turn and the model used. Never contains tool payloads. */
  trace: z.object({
    model: z.string().optional(),
    toolCalls: z.array(z.object({ name: z.string(), ok: z.boolean(), ms: z.number() })),
    interpretation: z.enum(['LLM', 'RULES', 'FORM', 'CONFIRMATION']),
    latencyMs: z.number(),
  }),
});
export type ChatResponse = z.infer<typeof ChatResponseSchema>;

export const ConfirmRequestSchema = z.object({
  pendingActionId: z.string(),
  decision: z.enum(['confirm', 'reject']),
  acknowledgedFindingIds: z.array(z.string()).default([]),
  conversationId: z.string().optional(),
});
export type ConfirmRequest = z.infer<typeof ConfirmRequestSchema>;

// ------------------------------------------------------------------ Voice
export const TranscribeSessionRequestSchema = z.object({
  language: LanguageCodeSchema.optional(),
  autoLanguageIdentification: z.boolean().default(false),
  sampleRateHz: z.union([z.literal(16000), z.literal(8000)]).default(16000),
});
export type TranscribeSessionRequest = z.infer<typeof TranscribeSessionRequestSchema>;

export const TranscribeSessionResponseSchema = z.object({
  voiceSessionId: z.string(),
  /** Presigned wss:// URL valid for a short window; single use is enforced by session id tracking. */
  websocketUrl: z.string().url(),
  expiresAt: z.string(),
  languageCode: z.string().optional(),
  identifyLanguage: z.boolean(),
  languageOptions: z.array(z.string()).optional(),
  sampleRateHz: z.number(),
  region: z.string(),
  maxDurationSeconds: z.number(),
});
export type TranscribeSessionResponse = z.infer<typeof TranscribeSessionResponseSchema>;

export const VoiceSessionReportSchema = z.object({
  voiceSessionId: z.string(),
  streamDurationMs: z.number().int().nonnegative(),
  outcome: z.enum(['COMMAND', 'CANCELLED', 'TIMEOUT', 'ERROR', 'EMPTY']),
  finalTranscriptLength: z.number().int().nonnegative().optional(),
});
export type VoiceSessionReport = z.infer<typeof VoiceSessionReportSchema>;

export const SpeakRequestSchema = z.object({
  text: z.string().min(1).max(1500),
  language: LanguageCodeSchema,
  voiceId: z.string().optional(),
});
export type SpeakRequest = z.infer<typeof SpeakRequestSchema>;

export const SpeakResponseSchema = z.object({
  audioBase64: z.string(),
  contentType: z.literal('audio/mpeg'),
  voiceId: z.string(),
  engine: z.literal('neural'),
  characters: z.number().int(),
});
export type SpeakResponse = z.infer<typeof SpeakResponseSchema>;

// ------------------------------------------------------------------ Session & preferences
export const MeResponseSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  userId: z.string(),
  displayName: z.string(),
  roles: z.array(z.string()),
  preferences: UserPreferencesSchema,
  voiceDefaults: z.object({
    shortcut: z.string(),
    pushToTalkShortcut: z.string(),
    silenceThresholdMs: z.number(),
    maxCommandSeconds: z.number(),
    allowedLanguages: z.array(z.string()),
    defaultLanguage: z.string(),
    arabicVoiceId: z.string(),
    englishVoiceId: z.string(),
  }),
  timeZone: z.string(),
  context: z.object({ activePatient: PatientBannerSchema.nullable(), activeEncounterId: z.string().nullable() }),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const ApiErrorSchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), details: z.unknown().nullable() }),
});
